import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { InspectorPanelAccordion } from "../../accordion/InspectorPanelAccordion"
import { InspectorPanelAccordionSubsections } from "../../accordion/InspectorPanelAccordionSubsections"
import { useEffect, useState } from "react"
import { useInspectorPanelMergedFlight } from "./useInspetorPanelMergedFlights"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"
import {
    selectAllMergedFilteredHotspots,
    selectAllMergedFilteredHotspotsBySensitivity,
} from "./selectors"
import { InspectorPanelHotspotLayerFiltersSensitivity } from "../filters/InspectorPanelHotspotLayerFiltersSensitivity"
import { InspectorPanelHotspotLayerFiltersDistanceToPerimeter } from "../filters/InspectorPanelHotspotLayerFiltersDistanceToPerimeter"
import { InspectorPanelHotspotLayerFiltersTemperature } from "../filters/InspectorPanelHotspotLayerFiltersTemperature"
import { InspectorPanelHotspotLayerFiltersSize } from "../filters/InspectorPanelHotspotLayerFiltersSize"
import { ButtonBorderless, ButtonTertiary } from "@nova/component-btn"
import { updatedMergedFlight } from "features/pubsub/entityActions/cubes/mergedFlights"
import * as Icons from "@nova/component-icons"
import { AlertContainer } from "@nova/component-alert-container"
import { debouncedDispatchHotspotUpdateActionForAllMergedFlights } from "./module"
import { updateInfraredFlight } from "features/pubsub/entityActions/flights/infrared"
import { useDebounceDispatch } from "tools/store"
import { HotspotStatus } from "@nova/constants"

export const InspectorPanelMergedFlightLayerFilters = ({
    id,
    openCategories,
    setCategoryIsOpen,
}: {
    id: string
    openCategories: Set<string>
    setCategoryIsOpen: (category: string, isOpen: boolean) => void
}) => {
    const { debounceDispatch } = useDebounceDispatch(500)
    const dispatch = useAppDispatch()
    const flight = useAppSelector(selectMergedFlightById(id))
    const mergedFlight = useAppSelector(selectMergedFlightById(id))
    const { hotspotFlightIds } = mergedFlight

    const hotspotsFilteredBySensitivity = useAppSelector(
        selectAllMergedFilteredHotspotsBySensitivity(hotspotFlightIds)
    ).filter((hotspot) => hotspot.status !== HotspotStatus.Removed)

    const { appearanceReferenceFlight } = useInspectorPanelMergedFlight(mergedFlight?.id ?? "")

    const filteredHotspots = useAppSelector(
        selectAllMergedFilteredHotspots(hotspotFlightIds)
    ).filter((hotspot) => hotspot.status !== HotspotStatus.Removed)
    const extraFeaturesJSON = appearanceReferenceFlight?.job?.extraFeatures

    const [resizerTrigger, triggerResize] = useState(0)

    useEffect(() => {
        triggerResize((prev) => ++prev)
    }, [id])

    const shouldShowWarning = (localStorage.getItem("hotspot_filter_warning") || "false") !== "true"
    return (
        <InspectorPanelAccordion
            title="Filters"
            subtitle={`Showing ${filteredHotspots.length} of ${hotspotsFilteredBySensitivity.length}`}
            isOpen={openCategories.has("filters")}
            setIsOpen={(isOpen) => setCategoryIsOpen("filters", isOpen)}
            hasSubsections={true}
            shouldPadChildrenVertically={false}
            extraVerticalPaddingInRem={0}
            extraResizeDependency={resizerTrigger}
        >
            <InspectorPanelAccordionSubsections>
                {/* The boolean logic is repeated because InspectorPanelAccordionSubsections
                    doesn't understand children wrapped in a fragment. */}

                {/* Based on all hotspots. */}
                {!!extraFeaturesJSON?.includes("sens|") && (
                    <div className="flex flex-col gap-2">
                        {flight.isSensitivitySynced && (
                            <>
                                {shouldShowWarning && (
                                    <AlertContainer
                                        type="warning"
                                        body="Filters you apply also apply for everyone on the project."
                                        onClose={() => {
                                            localStorage.setItem("hotspot_filter_warning", "true")
                                            setTimeout(() => triggerResize((prev) => ++prev), 0)
                                        }}
                                    />
                                )}
                                <div>
                                    <InspectorPanelHotspotLayerFiltersSensitivity
                                        flight={appearanceReferenceFlight!}
                                        updateFn={(updates) =>
                                            debouncedDispatchHotspotUpdateActionForAllMergedFlights(
                                                updateInfraredFlight,
                                                hotspotFlightIds,
                                                updates
                                            )
                                        }
                                    />
                                    <ButtonTertiary
                                        className="w-full mt-3"
                                        title="Unsync Sensitivity"
                                        onClick={() =>
                                            dispatch(
                                                updatedMergedFlight({
                                                    id,
                                                    updates: { isSensitivitySynced: false },
                                                })
                                            )
                                        }
                                        RightIcon={Icons.LinkOff}
                                    />
                                </div>
                            </>
                        )}
                        {!flight.isSensitivitySynced && (
                            <>
                                {shouldShowWarning && (
                                    <AlertContainer
                                        type="warning"
                                        body="Filters you apply also apply for everyone on the project."
                                        onClose={() => {
                                            localStorage.setItem("hotspot_filter_warning", "true")
                                            setTimeout(() => triggerResize((prev) => ++prev), 0)
                                        }}
                                    />
                                )}
                                <div className="flex justify-between items-center">
                                    <h5 className="text-white">Sensitivity</h5>
                                    <ButtonBorderless
                                        title="Sync Layers"
                                        onClick={() =>
                                            dispatch(
                                                updatedMergedFlight({
                                                    id,
                                                    updates: { isSensitivitySynced: true },
                                                })
                                            )
                                        }
                                        RightIcon={Icons.Sync}
                                        className="-m-2"
                                    />
                                </div>
                                {mergedFlight.hotspotFlights.map((flight, idx) => (
                                    <InspectorPanelHotspotLayerFiltersSensitivity
                                        key={idx}
                                        flight={flight}
                                        updateFn={(updates) =>
                                            debounceDispatch(
                                                updateInfraredFlight({ id: flight.id, updates })
                                            )
                                        }
                                        sliderTitle={flight.name}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                )}

                {/* Based on the subset of hotspots filtered by sensitivity. */}
                <InspectorPanelHotspotLayerFiltersDistanceToPerimeter
                    flight={appearanceReferenceFlight!}
                    updateFn={(updates) =>
                        debouncedDispatchHotspotUpdateActionForAllMergedFlights(
                            updateInfraredFlight,
                            hotspotFlightIds,
                            updates
                        )
                    }
                    triggerResize={() => setTimeout(() => triggerResize((prev) => ++prev), 0)}
                />

                {/* Based on the subset of hotspots filtered by sensitivity and distance to perimeter. */}
                {!!extraFeaturesJSON?.includes("hotspotThermal") && (
                    <InspectorPanelHotspotLayerFiltersTemperature
                        flight={appearanceReferenceFlight!}
                        updateFn={(updates) =>
                            debouncedDispatchHotspotUpdateActionForAllMergedFlights(
                                updateInfraredFlight,
                                hotspotFlightIds,
                                updates
                            )
                        }
                    />
                )}

                {/* Based on the subset of hotspots filtered by sensitivity and distance to perimeter. */}
                {!!extraFeaturesJSON?.includes("hotspotArea") && (
                    <InspectorPanelHotspotLayerFiltersSize
                        flight={appearanceReferenceFlight!}
                        updateFn={(updates) =>
                            debouncedDispatchHotspotUpdateActionForAllMergedFlights(
                                updateInfraredFlight,
                                hotspotFlightIds,
                                updates
                            )
                        }
                    />
                )}
            </InspectorPanelAccordionSubsections>
        </InspectorPanelAccordion>
    )
}
